import {
    Article,
    ArticleOutlined,
    HomeWork,
    HomeWorkOutlined,
    Settings,
    SettingsOutlined,
} from '@mui/icons-material';
import { Box, List, ListItem, Skeleton, Typography } from '@mui/material';
import { ComponentType, MouseEventHandler, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { matchPath, useLocation, useNavigate } from 'react-router-dom';

import {
    getPropertiesList,
    getSelectedPropertyId,
} from 'entities/Property/model/selectors/PropertySelectors';
import { UserRole, getUserRole } from 'entities/User';
import { getAuthIsLoading } from 'entities/User/model/selectors/userSelectors';
import { HelpMenu } from 'features/HelpMenu';
import { useNavigationBlock } from 'features/NavigationBlockModal/ui/NavigationBlockProvider/NavigationBlockProvider';
import { shouldShowVersionInfo } from 'shared/const/envVariables';
import {
    getRouteCatalog,
    getRouteCatalogRoot,
    getRouteMainReports,
    getRouteMainVendors,
    getRouteOrders,
    getRouteProperties,
    getRouteSettings,
    getRouteSettingsOrdersSourcing,
} from 'shared/const/router';
import { contentHeight } from 'shared/const/styles';
import { AppLink } from 'shared/ui/AppLink/AppLink';
import {
    IdCardIcon,
    IdCardOutlinedIcon,
    Package2Icon,
    Package2OutlinedIcon,
    TableChartViewIcon,
    TableChartViewOutlinedIcon,
} from 'shared/ui/icons';
import { VersionInfo } from 'widgets/VersionInfo';

import type { SvgIconProps } from '@mui/material/SvgIcon';

const NAV_WIDTH = '180px';

const navLabelSx = {
    fontSize: '1.4rem',
    fontWeight: 600,
    color: 'inherit',
};

const isCatalogPath = (pathname: string) =>
    Boolean(
        matchPath({ path: '/catalog', end: true }, pathname) ||
            matchPath(
                { path: '/catalog/:propertyId/*', end: false },
                pathname,
            ) ||
            matchPath(
                { path: '/properties/:propertyId/catalog/*', end: false },
                pathname,
            ),
    );

const isPropertiesInfoPath = (pathname: string) => {
    if (isCatalogPath(pathname)) {
        return false;
    }

    return Boolean(
        matchPath({ path: '/properties', end: true }, pathname) ||
            matchPath(
                { path: '/properties/:propertyId', end: true },
                pathname,
            ) ||
            matchPath(
                { path: '/properties/:propertyId/*', end: false },
                pathname,
            ),
    );
};

interface NavBarItem {
    id: string;
    label: string;
    route: string;
    iconIdle: ComponentType<SvgIconProps>;
    iconActive: ComponentType<SvgIconProps>;
    roles: UserRole[];
    isActive: (pathname: string) => boolean;
}

export const Navbar = () => {
    const { t } = useTranslation();
    const location = useLocation();
    const navigate = useNavigate();
    const { validateNavBlock } = useNavigationBlock();

    const userRole = useSelector(getUserRole);
    const selectedPropertyId = useSelector(getSelectedPropertyId);
    const propertiesList = useSelector(getPropertiesList);
    const userIsLoading = useSelector(getAuthIsLoading);

    const catalogPropertyId =
        selectedPropertyId || propertiesList?.[0]?.id || '';
    const catalogRoute = catalogPropertyId
        ? getRouteCatalog(catalogPropertyId)
        : getRouteCatalogRoot();
    const settingsRoute = catalogPropertyId
        ? getRouteSettingsOrdersSourcing(catalogPropertyId, 'operations')
        : getRouteSettings();

    const navBarItemsList = useMemo<NavBarItem[]>(
        () => [
            {
                id: 'orders',
                label: t('Orders'),
                route: getRouteOrders(),
                iconIdle: Package2OutlinedIcon,
                iconActive: Package2Icon,
                roles: [
                    UserRole.PMC_ADMIN,
                    UserRole.PROPERTY_MANAGER,
                    UserRole.MAINTENANCE_TECHNICIAN,
                    UserRole.MAINTENANCE_SUPERVISOR,
                ],
                isActive: (pathname) =>
                    Boolean(matchPath(getRouteOrders() + '/*', pathname)),
            },
            {
                id: 'catalog',
                label: t('Catalog'),
                route: catalogRoute,
                iconIdle: ArticleOutlined,
                iconActive: Article,
                roles: [
                    UserRole.PMC_ADMIN,
                    UserRole.PROPERTY_MANAGER,
                    UserRole.MAINTENANCE_TECHNICIAN,
                    UserRole.MAINTENANCE_SUPERVISOR,
                ],
                isActive: isCatalogPath,
            },
            {
                id: 'vendors',
                label: t('Vendors'),
                route: getRouteMainVendors(selectedPropertyId),
                iconIdle: IdCardOutlinedIcon,
                iconActive: IdCardIcon,
                roles: [
                    UserRole.PMC_ADMIN,
                    UserRole.PROPERTY_MANAGER,
                    UserRole.MAINTENANCE_TECHNICIAN,
                    UserRole.MAINTENANCE_SUPERVISOR,
                ],
                isActive: (pathname) =>
                    Boolean(
                        matchPath(
                            getRouteMainVendors(selectedPropertyId) + '/*',
                            pathname,
                        ) || matchPath('/vendors/*', pathname),
                    ),
            },
            {
                id: 'reports',
                label: t('Reports'),
                route: getRouteMainReports(),
                iconIdle: TableChartViewOutlinedIcon,
                iconActive: TableChartViewIcon,
                roles: [
                    UserRole.PMC_ADMIN,
                    UserRole.PROPERTY_MANAGER,
                    UserRole.MAINTENANCE_SUPERVISOR,
                ],
                isActive: (pathname) =>
                    Boolean(matchPath(getRouteMainReports() + '/*', pathname)),
            },
            {
                id: 'properties',
                label: t('Properties'),
                route: getRouteProperties(),
                iconIdle: HomeWorkOutlined,
                iconActive: HomeWork,
                roles: [
                    UserRole.PMC_ADMIN,
                    UserRole.PROPERTY_MANAGER,
                    UserRole.MAINTENANCE_TECHNICIAN,
                    UserRole.MAINTENANCE_SUPERVISOR,
                ],
                isActive: isPropertiesInfoPath,
            },
            {
                id: 'settings',
                label: t('Settings'),
                route: settingsRoute,
                iconIdle: SettingsOutlined,
                iconActive: Settings,
                roles: [UserRole.PMC_ADMIN, UserRole.PROPERTY_MANAGER],
                isActive: (pathname) =>
                    Boolean(matchPath(getRouteSettings() + '/*', pathname)),
            },
        ],
        [t, selectedPropertyId, catalogRoute, settingsRoute],
    );

    return (
        <>
            <Box
                sx={{
                    bgcolor: 'background.paper',
                    height: contentHeight,
                    width: NAV_WIDTH,
                    position: 'fixed',
                    zIndex: 'drawer',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                }}
            >
                <List sx={{ paddingTop: '24px', paddingBottom: 0 }}>
                    {!userIsLoading ? (
                        navBarItemsList.map((item) => {
                            const isActive = item.isActive(location.pathname);
                            const isHidden =
                                item.roles.includes(userRole) === false;
                            const Icon = isActive
                                ? item.iconActive
                                : item.iconIdle;

                            const disabledClick: MouseEventHandler = (e) => {
                                e.preventDefault();
                            };

                            if (isHidden) {
                                return null;
                            }

                            const navItemContent = (
                                <>
                                    <Icon
                                        sx={{
                                            fontSize: '2rem',
                                            color: isActive
                                                ? 'common.white'
                                                : '#868686',
                                        }}
                                    />
                                    <Typography sx={navLabelSx}>
                                        {item.label}
                                    </Typography>
                                </>
                            );

                            return (
                                <ListItem
                                    data-testid={`menu-${item.id}`}
                                    data-testclass={`nav-item-${item.id}`}
                                    sx={{
                                        padding: '0 0 0 20px',
                                        height: '44px',
                                        bgcolor: isActive
                                            ? 'primary.main'
                                            : 'transparent',
                                        color: isActive
                                            ? 'common.white'
                                            : '#868686',
                                        ':hover': !isActive
                                            ? {
                                                  bgcolor: 'primary.100',
                                                  color: 'primary.main',
                                                  cursor: 'pointer',
                                                  '& .MuiSvgIcon-root': {
                                                      color: 'primary.main',
                                                  },
                                                  '& .MuiTypography-root': {
                                                      color: 'primary.main',
                                                  },
                                              }
                                            : undefined,
                                    }}
                                    key={item.id}
                                >
                                    {isActive ? (
                                        <Box
                                            component="div"
                                            onClick={disabledClick}
                                            sx={{
                                                display: 'flex',
                                                gap: '12px',
                                                alignItems: 'center',
                                                width: '100%',
                                                height: '100%',
                                            }}
                                        >
                                            {navItemContent}
                                        </Box>
                                    ) : (
                                        <AppLink
                                            to={item.route}
                                            color="#868686"
                                            onClick={(e: React.MouseEvent) => {
                                                e.preventDefault();
                                                validateNavBlock(() => {
                                                    navigate(item.route);
                                                }, true);
                                            }}
                                            style={{
                                                display: 'flex',
                                                width: '100%',
                                                height: '100%',
                                                textDecoration: 'none',
                                                color: '#868686',
                                            }}
                                        >
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    gap: '12px',
                                                    alignItems: 'center',
                                                    width: '100%',
                                                    height: '100%',
                                                    cursor: 'pointer',
                                                }}
                                            >
                                                {navItemContent}
                                            </Box>
                                        </AppLink>
                                    )}
                                </ListItem>
                            );
                        })
                    ) : (
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '2px',
                            }}
                        >
                            <Skeleton variant="rectangular" height="44px" />
                            <Skeleton variant="rectangular" height="44px" />
                            <Skeleton variant="rectangular" height="44px" />
                        </Box>
                    )}
                </List>
                <Box sx={{ padding: '0 0 12px 0' }}>
                    <HelpMenu />
                    {shouldShowVersionInfo() && (
                        <Typography
                            align="center"
                            sx={{
                                marginTop: '12px',
                                marginRight: '24px',
                                opacity: '0.5',
                            }}
                        >
                            <VersionInfo />
                        </Typography>
                    )}
                </Box>
            </Box>
            <Box
                sx={{
                    height: contentHeight,
                    width: NAV_WIDTH,
                    flexShrink: 0,
                }}
            />
        </>
    );
};
